import { type ComponentType, type HTMLAttributes, memo, type ReactNode, useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';
import { Avatar } from '@air/primitive-avatar';

import { type GalleryItemType, type ItemProps } from '~/components/Gallery/types';
import { SelectableTableRow } from '~/components/TableView/SelectableTableRow';
import { ConfigurableTableCells } from '~/components/TableView/TableRow/ConfigurableTableCells';
import { type AssetCustomFieldTableCellProps } from '~/components/TableView/TableRow/CustomFieldTableCell/types';
import { type BasicTableRowProps } from '~/components/TableView/types';
import { assetTableRowInfo, getUploadedByAvatarProps } from '~/components/TableView/utils';
import { type ConfigViewOption } from '~/store/configViews/types';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';

export type AssetTableRowProps<C> = {
  assetProps: ItemProps;
  /** When set, the row shows these columns instead of the shared config view. */
  configViewOptions?: ConfigViewOption[];
  CustomFieldTableCell: ComponentType<AssetCustomFieldTableCellProps<C>>;
  renderNameCell: (_params: { isHovering: boolean; isSelected: boolean }) => ReactNode;
  keyboardListenerProps?: HTMLAttributes<HTMLDivElement>;
} & BasicTableRowProps<C>;

const _AssetTableRow = <C extends Clip | PublicClip>({
  data,
  assetProps,
  configViewOptions,
  CustomFieldTableCell,
  renderNameCell,
  keyboardListenerProps,
}: AssetTableRowProps<C>) => {
  const renderCustomFieldTableCellComponent = useCallback(
    (option: ConfigViewOption) => (
      <CustomFieldTableCell
        key={option.customFieldId}
        customFieldId={option.customFieldId ?? ''}
        customFieldName={option.fieldName}
        clip={data}
      />
    ),
    [CustomFieldTableCell, data],
  );

  const contextMenuOptions = useMemo(
    () => assetProps.getItemMenuOptions({ trackLocation: 'table-item-context-menu' }),
    [assetProps],
  );

  const item = useMemo(() => {
    const type = getAssetGalleryItemType(data);
    /**
     * Had to declare the type explicitly here cause useMemo screws types up
     */
    const item: {
      id: string;
      type: GalleryItemType.file | GalleryItemType.asset;
      item: C;
    } = {
      id: data.id,
      type,
      item: data,
    };

    return item;
  }, [data]);

  const actorAvatar = useMemo(() => <Avatar {...getUploadedByAvatarProps(data)} />, [data]);

  const renderCells = useCallback(
    ({ isSelected, isHovering }: { isSelected: boolean; isHovering: boolean }) => (
      <>
        {renderNameCell({ isHovering, isSelected })}
        <ConfigurableTableCells
          options={configViewOptions}
          renderCustomFieldTableCellComponent={renderCustomFieldTableCellComponent}
          rowInfo={assetTableRowInfo(data)}
          actorAvatar={actorAvatar}
        />
      </>
    ),
    [actorAvatar, configViewOptions, data, renderCustomFieldTableCellComponent, renderNameCell],
  );

  const onRowClick = useCallback(() => assetProps.onItemClick('table-cell'), [assetProps]);

  return (
    <SelectableTableRow
      isSelectable={assetProps.isSelectable}
      item={item}
      onRowClick={onRowClick}
      getSelectionMenuOptions={assetProps.getSelectionMenuOptions}
      contextMenuOptions={contextMenuOptions}
      renderCells={renderCells}
      keyboardListenerProps={keyboardListenerProps}
    />
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const AssetTableRow = memo(_AssetTableRow) as typeof _AssetTableRow;
